/**
 * Step3Vehicle Component
 *
 * Collects:
 * - Vehicle year (number input)
 * - Vehicle make (text input)
 * - Vehicle model (text input)
 * - Purchase date (text input with MM/YYYY format)
 * - Bought condition (select: New / Used / Certified Pre-Owned)
 * - Original purchase method (select: Paid in full / Financed / Leased)
 * - Current payoff status (select: Still financed / Still leased / Fully paid off)
 * - Lien holder (text input, conditional: only when still financed or still leased)
 * - Annual mileage (number input with range slider)
 *
 * Shows cross-field warning if vehicle is paid off but was originally financed/leased.
 * Uses react-hook-form's useFormContext and Controller for managed state.
 * Validates against step3Schema from formSchemas.
 */

import { useEffect } from 'react';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { Emery } from '@/components/ui/Calla';
import { EMERY_MESSAGES } from '@/constants/callaMessages';
import { FormField } from '@/components/ui/FormField';
import { AnimatedReveal } from '@/components/ui/AnimatedReveal';
import { InfoBanner } from '@/components/ui/InfoBanner';
import {
  getCrossFieldWarnings,
  VEHICLE_USE_OPTIONS,
  VEHICLE_USE_LABELS,
  MAX_ADDITIONAL_VEHICLES,
  type FullFormData,
} from '@/schemas/formSchemas';
import { AdditionalVehicleBlock } from '@/components/steps/AdditionalVehicleBlock';

export default function Step3Vehicle() {
  const { control, formState: { errors }, watch, setValue } = useFormContext<FullFormData>();
  const formValues = watch();
  const boughtCondition = watch('step3.vehicleBoughtCondition');
  const purchaseMethod = watch('step3.vehicleOriginalPurchaseMethod');
  const payoffStatus = watch('step3.vehicleCurrentPayoffStatus');
  const primaryVehicleUse = watch('step3.primaryVehicleUse');

  const { fields: additionalVehicles, append, remove } = useFieldArray({
    control,
    name: 'step3.additionalVehicles',
  });
  const canAddVehicle = additionalVehicles.length < MAX_ADDITIONAL_VEHICLES;

  const paidInFull = purchaseMethod === 'paid_in_full';
  // Show lien holder input only if still financed or still leased
  const showLienHolder = !paidInFull && (payoffStatus === 'still_financed' || payoffStatus === 'still_leased');

  // When user selects Paid in Full, auto-fill payoff status and clear lien holder.
  // When they switch away, clear payoff so they re-answer.
  useEffect(() => {
    if (paidInFull) {
      if (payoffStatus !== 'fully_paid_off') {
        setValue('step3.vehicleCurrentPayoffStatus', 'fully_paid_off', { shouldValidate: true });
      }
      setValue('step3.vehicleLienHolder', '');
    } else if (payoffStatus === 'fully_paid_off') {
      // user switched away from paid_in_full; force re-answer
      setValue('step3.vehicleCurrentPayoffStatus', '' as FullFormData['step3']['vehicleCurrentPayoffStatus']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidInFull]);

  // Check for cross-field warnings
  const warnings = getCrossFieldWarnings(formValues.step3 || {});
  const vehicleWarnings = warnings.filter((w) => w.field === 'vehicleCurrentPayoffStatus');

  return (
    <div>
      <Emery message={EMERY_MESSAGES.step3} />

      {/* Vehicle Year */}
      <FormField
        label="Year"
        name="step3.vehicleYear"
        required={true}
        error={errors.step3?.vehicleYear?.message}
        helperText="1980 to present"
      >
        <Controller
          name="step3.vehicleYear"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoComplete="off"
              enterKeyHint="next"
              placeholder={new Date().getFullYear().toString()}
              className={`form-input ${
                errors.step3?.vehicleYear ? 'form-input-error' : ''
              }`}
              onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : '')}
            />
          )}
        />
      </FormField>

      {/* Vehicle Make */}
      <FormField
        label="Make"
        name="step3.vehicleMake"
        required={true}
        error={errors.step3?.vehicleMake?.message}
        helperText="e.g., Toyota, BMW, Ford"
      >
        <Controller
          name="step3.vehicleMake"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="off"
              autoCapitalize="words"
              enterKeyHint="next"
              placeholder="Toyota"
              className={`form-input ${
                errors.step3?.vehicleMake ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Vehicle Model */}
      <FormField
        label="Model"
        name="step3.vehicleModel"
        required={true}
        error={errors.step3?.vehicleModel?.message}
        helperText="e.g., Camry, X3, F-150"
      >
        <Controller
          name="step3.vehicleModel"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="off"
              autoCapitalize="words"
              enterKeyHint="next"
              placeholder="Camry"
              className={`form-input ${
                errors.step3?.vehicleModel ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Purchase Date */}
      <FormField
        label="Purchase Date"
        name="step3.vehiclePurchaseDate"
        required={true}
        error={errors.step3?.vehiclePurchaseDate?.message}
        helperText="Format: MM/YYYY (e.g., 03/2022)"
      >
        <Controller
          name="step3.vehiclePurchaseDate"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              inputMode="numeric"
              pattern="[0-9/]*"
              maxLength={7}
              autoComplete="off"
              enterKeyHint="next"
              placeholder="MM/YYYY"
              className={`form-input ${
                errors.step3?.vehiclePurchaseDate ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Bought Condition */}
      <FormField
        label="Bought As"
        name="step3.vehicleBoughtCondition"
        required={true}
        error={errors.step3?.vehicleBoughtCondition?.message}
      >
        <Controller
          name="step3.vehicleBoughtCondition"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                errors.step3?.vehicleBoughtCondition ? 'form-input-error' : ''
              }`}
            >
              <option value="">Select condition</option>
              <option value="certified_pre_owned">Certified Pre-Owned</option>
              <option value="new">New</option>
              <option value="used">Used</option>
            </select>
          )}
        />
      </FormField>

      <AnimatedReveal isVisible={boughtCondition === 'certified_pre_owned'}>
        <p className="-mt-4 mb-6 text-xs" style={{ color: 'var(--text-secondary)' }}>
          Certified Pre-Owned (CPO) means a used vehicle inspected and backed by the manufacturer's warranty.
        </p>
      </AnimatedReveal>

      {/* Original Purchase Method */}
      <FormField
        label="How Did You Purchase It?"
        name="step3.vehicleOriginalPurchaseMethod"
        required={true}
        error={errors.step3?.vehicleOriginalPurchaseMethod?.message}
      >
        <Controller
          name="step3.vehicleOriginalPurchaseMethod"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                errors.step3?.vehicleOriginalPurchaseMethod ? 'form-input-error' : ''
              }`}
            >
              <option value="">Select method</option>
              <option value="financed">Financed</option>
              <option value="leased">Leased</option>
              <option value="paid_in_full">Paid in Full</option>
            </select>
          )}
        />
      </FormField>

      {/* Current Payoff Status — hidden when purchase method is Paid in Full */}
      <AnimatedReveal isVisible={!paidInFull}>
        <FormField
          label="Current Payoff Status"
          name="step3.vehicleCurrentPayoffStatus"
          required={true}
          error={errors.step3?.vehicleCurrentPayoffStatus?.message}
          whyText="If you still owe money or have a lease, your carrier requires certain coverage."
        >
          <Controller
            name="step3.vehicleCurrentPayoffStatus"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                className={`form-input ${
                  errors.step3?.vehicleCurrentPayoffStatus ? 'form-input-error' : ''
                }`}
              >
                <option value="">Select status</option>
                <option value="fully_paid_off">Fully Paid Off</option>
                <option value="still_financed">Still Financed</option>
                <option value="still_leased">Still Leased</option>
              </select>
            )}
          />
        </FormField>
      </AnimatedReveal>

      {/* Cross-field warning: paid off but originally financed */}
      <AnimatedReveal isVisible={vehicleWarnings.length > 0}>
        <div className="mb-6">
          {vehicleWarnings.map((warning, idx) => (
            <InfoBanner
              key={idx}
              type={warning.type as 'info' | 'savings_opportunity' | 'warning'}
              message={warning.message}
            />
          ))}
        </div>
      </AnimatedReveal>

      {/* Conditional: Lien Holder */}
      <AnimatedReveal isVisible={showLienHolder}>
        <FormField
          label="Lien Holder / Lessor Name"
          name="step3.vehicleLienHolder"
          required={true}
          error={errors.step3?.vehicleLienHolder?.message}
          helperText="The name of the bank, finance company, or leasing company"
          whyText="Lenders and lessors must be listed on your insurance policy."
        >
          <Controller
            name="step3.vehicleLienHolder"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                autoComplete="organization"
                autoCapitalize="words"
                enterKeyHint="next"
                placeholder="Chase Bank, Wells Fargo, BMW Financial Services, etc."
                className={`form-input ${
                  errors.step3?.vehicleLienHolder ? 'form-input-error' : ''
                }`}
              />
            )}
          />
        </FormField>
      </AnimatedReveal>

      {/* Annual Mileage */}
      <FormField
        label="Annual Mileage"
        name="step3.annualMileage"
        required={true}
        error={errors.step3?.annualMileage?.message}
        helperText="Estimated miles you drive per year"
      >
        <Controller
          name="step3.annualMileage"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="12000"
              className={`form-input ${
                errors.step3?.annualMileage ? 'form-input-error' : ''
              }`}
              onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : '')}
            />
          )}
        />
      </FormField>
      {/* Primary Vehicle Use */}
      <FormField
        label="Primary use of this vehicle"
        name="step3.primaryVehicleUse"
        required={true}
        error={errors.step3?.primaryVehicleUse?.message}
      >
        <Controller
          name="step3.primaryVehicleUse"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                errors.step3?.primaryVehicleUse ? 'form-input-error' : ''
              }`}
            >
              <option value="">Select use</option>
              {VEHICLE_USE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {VEHICLE_USE_LABELS[option]}
                </option>
              ))}
            </select>
          )}
        />
      </FormField>

      {/* Conditional: Commute Mileage */}
      <AnimatedReveal isVisible={primaryVehicleUse === 'commute'}>
        <FormField
          label="One-way commute distance (miles)"
          name="step3.commuteMileage"
          required={true}
          error={errors.step3?.commuteMileage?.message}
        >
          <Controller
            name="step3.commuteMileage"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="15"
                className={`form-input ${
                  errors.step3?.commuteMileage ? 'form-input-error' : ''
                }`}
                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : '')}
              />
            )}
          />
        </FormField>
      </AnimatedReveal>

      {/* Additional vehicles */}
      {additionalVehicles.length > 0 && (
        <div
          className="mt-8 mb-4 text-sm font-semibold"
          style={{ color: 'var(--color-navy)' }}
        >
          Other vehicles on your policy
        </div>
      )}
      {additionalVehicles.map((fieldItem, index) => (
        <AdditionalVehicleBlock
          key={fieldItem.id}
          index={index}
          onRemove={() => remove(index)}
        />
      ))}

      {canAddVehicle && (
        <button
          type="button"
          onClick={() =>
            append({
              vehicleYear: undefined as unknown as number,
              vehicleMake: '',
              vehicleModel: '',
              vehiclePurchaseDate: '',
              vehicleBoughtCondition: '' as FullFormData['step3']['vehicleBoughtCondition'],
              vehicleOriginalPurchaseMethod:
                '' as FullFormData['step3']['vehicleOriginalPurchaseMethod'],
              vehicleCurrentPayoffStatus:
                '' as FullFormData['step3']['vehicleCurrentPayoffStatus'],
              vehicleLienHolder: '',
              annualMileage: undefined as unknown as number,
              primaryVehicleUse:
                '' as FullFormData['step3']['primaryVehicleUse'],
              commuteMileage: undefined,
            })
          }
          className="w-full mt-2 mb-4 flex items-center justify-center gap-2"
          style={{
            border: '1px dashed var(--border-default, #D1D5DB)',
            background: 'white',
            padding: '14px',
            borderRadius: 12,
            color: 'var(--color-primary)',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <Plus size={18} strokeWidth={2} />
          Add another vehicle
        </button>
      )}
    </div>
  );
}
